import { useState } from 'react';

export default function TrustSection() {
  const [email, setEmail] = useState('');
  const [status, setStatus] = useState<'idle' | 'success' | 'error'>('idle');
  const [errorMsg, setErrorMsg] = useState('');

  const handleSubmit = async (e: React.FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    const form = e.currentTarget;
    const formData = new FormData(form);
    const honeypot = formData.get('phone_alt')?.toString().trim() || '';

    if (honeypot) {
      setStatus('success');
      setEmail('');
      return;
    }

    try {
      const response = await fetch('https://readdy.ai/api/form/d9q8ef4nlsngrm4lltbg', {
        method: 'POST',
        headers: { 'Content-Type': 'application/x-www-form-urlencoded' },
        body: new URLSearchParams({
          email: formData.get('email')?.toString() || '',
        }).toString(),
      });

      const responseText = await response.text();
      let parsed;
      try {
        parsed = JSON.parse(responseText);
      } catch {
        parsed = null;
      }

      if (response.ok && parsed?.code === 'OK') {
        setStatus('success');
        setEmail('');
      } else {
        const msg = parsed?.meta?.message || parsed?.message || 'Something went wrong. Please try again.';
        if (msg.toLowerCase().includes('spam')) {
          setStatus('success');
        } else {
          setErrorMsg(msg);
          setStatus('error');
        }
      }
    } catch {
      setErrorMsg('Network error. Please try again.');
      setStatus('error');
    }
  };

  return (
    <section className="py-16 md:py-20 bg-background-50">
      <div className="max-w-7xl mx-auto px-4 md:px-6">
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-10 md:gap-14 items-center">
          <div>
            <span className="inline-block px-4 py-1 rounded-full bg-accent-100 text-accent-700 text-xs font-label font-medium mb-4">
              Why Choose Us
            </span>
            <h2 className="text-2xl md:text-4xl font-heading font-semibold text-foreground-900 mb-4">
              Why Users Choose Sugargoo Spreadsheet
            </h2>
            <p className="text-foreground-600 text-sm md:text-base leading-relaxed mb-8">
              The Sugargoo Spreadsheet transforms how you discover products. Instead of spending hours browsing randomly, our organized database lets you find trending items in minutes.
            </p>

            <div className="overflow-hidden rounded-xl border border-background-200/70">
              <table className="w-full text-sm">
                <thead>
                  <tr className="bg-background-100">
                    <th className="text-left px-4 py-3 font-label font-semibold text-foreground-800 text-xs uppercase tracking-wider">
                      Feature
                    </th>
                    <th className="text-left px-4 py-3 font-label font-semibold text-foreground-400 text-xs uppercase tracking-wider">
                      Traditional Search
                    </th>
                    <th className="text-left px-4 py-3 font-label font-semibold text-primary-700 text-xs uppercase tracking-wider">
                      Sugargoo Spreadsheet
                    </th>
                  </tr>
                </thead>
                <tbody>
                  <tr className="border-t border-background-200/50">
                    <td className="px-4 py-3 text-foreground-700 font-medium">Product Discovery</td>
                    <td className="px-4 py-3 text-foreground-500">Slow, random browsing</td>
                    <td className="px-4 py-3 text-primary-700 font-medium">Fast, organized finds</td>
                  </tr>
                  <tr className="border-t border-background-200/50">
                    <td className="px-4 py-3 text-foreground-700 font-medium">Organization</td>
                    <td className="px-4 py-3 text-foreground-500">Scattered information</td>
                    <td className="px-4 py-3 text-primary-700 font-medium">Structured database</td>
                  </tr>
                  <tr className="border-t border-background-200/50">
                    <td className="px-4 py-3 text-foreground-700 font-medium">Beginner Friendly</td>
                    <td className="px-4 py-3 text-foreground-500">Steep learning curve</td>
                    <td className="px-4 py-3 text-primary-700 font-medium">Easy to start</td>
                  </tr>
                  <tr className="border-t border-background-200/50">
                    <td className="px-4 py-3 text-foreground-700 font-medium">Research Time</td>
                    <td className="px-4 py-3 text-foreground-500">Hours of searching</td>
                    <td className="px-4 py-3 text-primary-700 font-medium">Minutes to discover</td>
                  </tr>
                </tbody>
              </table>
            </div>
          </div>

          <div className="bg-background-100 border border-background-200/70 rounded-2xl p-6 md:p-8">
            <h3 className="text-xl md:text-2xl font-heading font-semibold text-foreground-900 mb-2">
              Is Sugargoo Legit &amp; Safe?
            </h3>
            <p className="text-sm text-foreground-600 mb-6 leading-relaxed">
              This is an independent information website. We provide unbiased research and guides about Sugargoo Spreadsheet. We do not collect payments, sell products, or claim official affiliation. Always verify sellers before making purchases.
            </p>

            <div className="space-y-3 mb-8">
              {[
                { icon: 'ri-shield-check-line', text: 'Independent &amp; Unbiased Research Resource' },
                { icon: 'ri-lock-line', text: 'No Payment Collection On This Website' },
                { icon: 'ri-information-line', text: 'Educational Shopping Guides &amp; Tips' },
                { icon: 'ri-user-heart-line', text: 'Beginner-Friendly Learning Materials' },
              ].map((item, idx) => (
                <div key={idx} className="flex items-start gap-3">
                  <div className="w-8 h-8 flex items-center justify-center bg-accent-100 rounded-lg flex-shrink-0 mt-0.5">
                    <i className={`${item.icon} text-accent-600 text-sm`}></i>
                  </div>
                  <span className="text-sm text-foreground-700">{item.text}</span>
                </div>
              ))}
            </div>

            <div className="bg-background-50 border border-background-200/70 rounded-xl p-5">
              <h4 className="font-heading text-lg font-semibold text-foreground-900 mb-2">
                Stay Updated
              </h4>
              <p className="text-xs text-foreground-500 mb-4">
                Get the latest Sugargoo Spreadsheet updates and shopping tips.
              </p>
              <form onSubmit={handleSubmit} className="flex flex-col sm:flex-row gap-2" data-readdy-form="">
                <input
                  type="text"
                  name="phone_alt"
                  tabIndex={-1}
                  autoComplete="off"
                  aria-hidden="true"
                  readOnly
                  className="contact-alt-field"
                />
                <input
                  type="email"
                  name="email"
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                  placeholder="Your email address"
                  required
                  className="flex-1 px-4 py-2.5 rounded-full border border-background-200 bg-background-50 text-sm text-foreground-900 placeholder:text-foreground-400 focus:outline-none focus:border-primary-400 transition-colors font-body"
                />
                <button
                  type="submit"
                  className="px-6 py-2.5 rounded-full bg-primary-500 text-white text-sm font-label font-semibold hover:bg-primary-600 transition-colors cursor-pointer whitespace-nowrap"
                >
                  Subscribe
                </button>
              </form>
              {status === 'success' && (
                <p className="text-xs text-accent-600 mt-3 font-label">Thanks for subscribing! Check your inbox.</p>
              )}
              {status === 'error' && (
                <p className="text-xs text-red-500 mt-3 font-label">{errorMsg}</p>
              )}
            </div>
          </div>
        </div>
      </div>
    </section>
  );
}